import { useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copy-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { formatDateTime } from "@/lib/format";

type CreateApiKeyResponse = components["schemas"]["CreateApiKeyResponse"];

interface CreateApiKeyDialogProps {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

export function CreateApiKeyDialog({
  clientId,
  open,
  onOpenChange,
  onCreated,
}: CreateApiKeyDialogProps) {
  const [name, setName] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateApiKeyResponse | null>(null);

  function resetForm() {
    setName("");
    setExpiresAt("");
    setSubmitting(false);
    setError(null);
    setResult(null);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    let expiresAtIso: string | null = null;
    if (expiresAt) {
      const parsed = new Date(expiresAt);
      if (Number.isNaN(parsed.getTime())) {
        setError("Invalid expiry date.");
        setSubmitting(false);
        return;
      }
      if (parsed.getTime() <= Date.now()) {
        setError("Expiry must be in the future.");
        setSubmitting(false);
        return;
      }
      expiresAtIso = parsed.toISOString();
    }

    const { data, error: apiError } = await api.POST("/admin/api-keys", {
      body: {
        clientId,
        name: name.trim() || null,
        expiresAt: expiresAtIso,
      },
    });

    if (apiError) {
      setError("Failed to create API key.");
      setSubmitting(false);
      return;
    }

    setResult(data!);
    setSubmitting(false);
    onCreated();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>API Key Created</DialogTitle>
              <DialogDescription>
                Copy this key now — it will not be shown again.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 text-sm">
              {result.name && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Name</span>
                  <span>{result.name}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Prefix</span>
                <code className="font-mono text-xs">{result.keyPrefix}</code>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created</span>
                <span>{formatDateTime(result.createdAt)}</span>
              </div>
              {result.expiresAt && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expires</span>
                  <span>{formatDateTime(result.expiresAt)}</span>
                </div>
              )}
              <div className="space-y-1.5">
                <span className="text-muted-foreground">API Key</span>
                <div className="bg-muted flex items-center gap-2 rounded-md p-2">
                  <code className="flex-1 break-all text-xs">{result.rawKey}</code>
                  <CopyButton value={result.rawKey} />
                </div>
              </div>
            </div>

            <DialogFooter showCloseButton />
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Create API Key</DialogTitle>
              <DialogDescription>
                Generate a new API key scoped to this client.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="api-key-name">Name (optional)</Label>
                <Input
                  id="api-key-name"
                  placeholder="e.g. Production integration"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="api-key-expires-at">Expires (optional)</Label>
                <Input
                  id="api-key-expires-at"
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              </div>
              {error && <p className="text-destructive text-sm">{error}</p>}
            </div>

            <DialogFooter className="mt-6">
              <Button type="submit" disabled={submitting}>
                {submitting && <Spinner className="size-4" />}
                Create
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
